import type {Relation} from "typeorm";
import {Column, Entity, ManyToOne, TableInheritance} from "typeorm";
import {BaseEntity} from "@/modules/base/entity/BaseEntity";
import {Max, Min} from "class-validator";
import {Team} from "@/modules/team/entity";
import {Season} from "@/modules/season/entity";
import {CanNegotiateInterface} from "@/modules/diplomacy/interfaces";
import {NegotiationStatusEnum} from "@/modules/diplomacy/enums";
import {roundFront} from "@/modules/base/services/chance";
import {CurrencyMetaType} from "@/modules/action-points/interfaces";
import {BankCategory} from "@/modules/finances/enums";

@Entity()
@TableInheritance({column: {type: "varchar", name: "type"}})
export class Negotiation extends BaseEntity {
  target: CanNegotiateInterface;

  @ManyToOne(() => Season)
  season: Relation<Season>;

  @ManyToOne(() => Team, team => team.negotiations)
  team: Relation<Team>;

  @Column({type: 'integer'})
  monthlyFrom: number;

  @Column({type: 'integer'})
  monthlyTo: number;

  @Column({type: 'integer'})
  onceFrom: number;

  @Column({type: 'integer'})
  onceTo: number;

  @Column({type: 'varchar', enum: NegotiationStatusEnum})
  status: NegotiationStatusEnum = NegotiationStatusEnum.OPENED;

  @Column({type: 'integer'})
  @Min(0)
  @Max(100)
  progress = 0;

  @Column({type: 'integer'})
  @Min(0)
  @Max(100)
  patience = 100;

  async init() {
    await this.load('target');

    return;
  }

  get gateMeta(): CurrencyMetaType {
    return {
      category: BankCategory.TRANSFERS,
      label: translator.t('close_negotiation'),
    };
  }

  get progressCompleted() {
    return this.progress === 100;
  }

  get canBeClosed() {
    return !this.progressCloseLimit || this.progress >= this.progressCloseLimit;
  }

  get progressCloseLimit(): number | undefined {
    return this.target.resentfulness;
  }

  get patienceAbortLimit(): number {
    return Math.round(50 - this.target.patience / 2);
  }

  get once(): number {
    if (this.onceTo <= this.onceFrom) {
      return roundFront(Math.round(this.onceFrom - (this.onceFrom - this.onceTo) * this.progress / 100));
    }

    return roundFront(Math.round(this.onceFrom + (this.onceTo - this.onceFrom) * this.progress / 100));
  }

  get monthly(): number {
    if (this.monthlyTo <= this.monthlyFrom) {
      return roundFront(Math.round(this.monthlyFrom - (this.monthlyFrom - this.monthlyTo) * this.progress / 100));
    }

    return roundFront(Math.round(this.monthlyFrom + (this.monthlyTo - this.monthlyFrom) * this.progress / 100));
  }

  get total(): number {
    return this.once;
  }
}